import { jsPDF } from 'jspdf';
import { LabelLayout, LABEL_DIMENSIONS } from '@/types/shipping';

export interface TestPrintOptions {
  printerName: string;
  printerType: 'label' | 'letter' | 'unknown';
  labelSize?: LabelLayout;
  includeBarcode?: boolean;
  includeGraphics?: boolean;
}

export class TestPrintService {

  /**
   * Clean data URL to remove filename parameter that UHB doesn't support
   * Converts: data:application/pdf;filename=generated.pdf;base64,<data>
   * To: data:application/pdf;base64,<data>
   */
  private static cleanDataUrl(dataUrl: string): string {
    // Remove filename parameter between MIME type and base64
    return dataUrl.replace(/;filename=[^;]*/, '');
  }

  /**
   * Generate a test PDF based on printer type
   */
  static generateTestPdf(options: TestPrintOptions): string {
    if (options.printerType === 'label') {
      return this.generateLabelTestPdf(options);
    } else {
      return this.generateLetterTestPdf(options);
    }
  }

  /**
   * Generate a label test PDF with configurable size
   */
  private static generateLabelTestPdf(options: TestPrintOptions): string {
    // Get label dimensions based on labelSize option, default to 4x6
    const labelSize: LabelLayout = options.labelSize || '4x6';
    const dimensions = LABEL_DIMENSIONS[labelSize];

    // Create PDF with the specified dimensions
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [dimensions.widthMm, dimensions.heightMm]
    });

    const currentTime = new Date();
    const dateStr = currentTime.toLocaleDateString();
    const timeStr = currentTime.toLocaleTimeString();

    // Calculate positions based on label dimensions
    const width = dimensions.widthMm;
    const height = dimensions.heightMm;
    const centerX = width / 2;
    const margin = 10;
    const rightEdge = width - margin;

    // Set margins and basic styling
    pdf.setFont('helvetica', 'normal');

    // Title - Large and Bold
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('PRINTER TEST LABEL', centerX, 15, { align: 'center' });

    // Horizontal line
    pdf.setLineWidth(0.5);
    pdf.line(margin, 20, rightEdge, 20);

    // Printer information
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Printer:', margin, 30);
    pdf.setFont('helvetica', 'bold');
    pdf.text(options.printerName, 30, 30);

    pdf.setFont('helvetica', 'normal');
    pdf.text('Date:', margin, 38);
    pdf.text(dateStr, 25, 38);

    pdf.text('Time:', margin, 46);
    pdf.text(timeStr, 25, 46);

    pdf.text('Type:', margin, 54);
    pdf.text('Label Printer Test', 25, 54);

    pdf.text('Size:', margin, 62);
    pdf.text(`${labelSize} (${dimensions.width}" x ${dimensions.height}")`, 25, 62);

    // Test different font sizes
    pdf.setFont('helvetica', 'bold');
    pdf.text('Font Size Tests:', margin, 76);

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Small text (8pt) - The quick brown fox', margin, 84);

    pdf.setFontSize(10);
    pdf.text('Normal text (10pt) - jumps over lazy dog', margin, 92);

    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Large text (14pt) - TESTING', margin, 100);

    // Add barcode if requested
    if (options.includeBarcode) {
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Test Barcode:', margin, 114);

      // Simple barcode representation (actual barcode would require a library)
      pdf.setFont('courier', 'normal');
      pdf.text('|||| || |||| | || |||| ||||', margin, 122);
      pdf.text('123456789012', margin, 130);
    }

    // Add graphics/border if requested
    if (options.includeGraphics) {
      // Border around entire label
      pdf.setLineWidth(1);
      pdf.rect(5, 5, width - 10, height - 10);

      // Corner markers
      pdf.setLineWidth(0.5);
      pdf.line(5, 5, 15, 5);
      pdf.line(5, 5, 5, 15);

      pdf.line(width - 15, 5, width - 5, 5);
      pdf.line(width - 5, 5, width - 5, 15);

      pdf.line(5, height - 20, 5, height - 10);
      pdf.line(5, height - 10, 15, height - 10);

      pdf.line(width - 15, height - 10, width - 5, height - 10);
      pdf.line(width - 5, height - 20, width - 5, height - 10);
    }

    // Footer (positioned relative to label height)
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'italic');
    pdf.text('Universal Hardware Bridge Test Print', centerX, height - 12, { align: 'center' });
    pdf.text(`Generated: ${currentTime.toISOString()}`, centerX, height - 7, { align: 'center' });

    // Return as data URL (cleaned for UHB compatibility)
    return this.cleanDataUrl(pdf.output('dataurlstring'));
  }

  /**
   * Generate an 8.5x11 inch letter test PDF
   */
  private static generateLetterTestPdf(options: TestPrintOptions): string {
    // Standard letter size PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter'
    });

    const currentTime = new Date();
    const dateStr = currentTime.toLocaleDateString();
    const timeStr = currentTime.toLocaleTimeString();

    // Title
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text('PRINTER TEST PAGE', 105, 30, { align: 'center' });

    // Subtitle
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Universal Hardware Bridge Test Print', 105, 40, { align: 'center' });

    // Horizontal line
    pdf.setLineWidth(1);
    pdf.line(20, 45, 195, 45);

    // Printer Information Section
    let yPos = 60;
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Printer Information', 20, yPos);

    yPos += 15;
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Printer Name: ${options.printerName}`, 25, yPos);

    yPos += 10;
    pdf.text(`Printer Type: ${options.printerType}`, 25, yPos);

    yPos += 10;
    pdf.text(`Test Date: ${dateStr}`, 25, yPos);

    yPos += 10;
    pdf.text(`Test Time: ${timeStr}`, 25, yPos);

    // Typography Test Section
    yPos += 25;
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Typography Tests', 20, yPos);

    yPos += 15;
    // Font sizes
    const fontSizes = [8, 10, 12, 14, 16, 18, 20];
    fontSizes.forEach(size => {
      pdf.setFontSize(size);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Font size ${size}pt: The quick brown fox jumps over the lazy dog`, 25, yPos);
      yPos += size * 0.6 + 3;
    });

    // Font styles
    yPos += 10;
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Normal text: ABCDEFGHIJKLMNOPQRSTUVWXYZ abcdefghijklmnopqrstuvwxyz 0123456789', 25, yPos);

    yPos += 10;
    pdf.setFont('helvetica', 'bold');
    pdf.text('Bold text: ABCDEFGHIJKLMNOPQRSTUVWXYZ abcdefghijklmnopqrstuvwxyz 0123456789', 25, yPos);

    yPos += 10;
    pdf.setFont('helvetica', 'italic');
    pdf.text('Italic text: ABCDEFGHIJKLMNOPQRSTUVWXYZ abcdefghijklmnopqrstuvwxyz 0123456789', 25, yPos);

    // Alignment tests
    yPos += 20;
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Alignment Tests', 20, yPos);

    yPos += 15;
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Left aligned text', 25, yPos);
    pdf.text('Center aligned text', 105, yPos, { align: 'center' });
    pdf.text('Right aligned text', 185, yPos, { align: 'right' });

    // Graphics test
    if (options.includeGraphics) {
      yPos += 20;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Graphics Tests', 20, yPos);

      yPos += 15;
      // Draw some basic shapes
      pdf.setLineWidth(1);

      // Rectangle
      pdf.rect(25, yPos, 30, 20);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Rectangle', 40, yPos + 12, { align: 'center' });

      // Line
      pdf.line(70, yPos, 100, yPos + 20);
      pdf.text('Line', 85, yPos + 25, { align: 'center' });

      // Circle (approximated)
      pdf.circle(130, yPos + 10, 10);
      pdf.text('Circle', 130, yPos + 25, { align: 'center' });
    }

    // System Information
    yPos += 40;
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('System Information', 20, yPos);

    yPos += 12;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Browser: ${navigator.userAgent}`, 25, yPos, { maxWidth: 160 });

    yPos += 15;
    pdf.text(`Screen Resolution: ${screen.width}x${screen.height}`, 25, yPos);

    yPos += 10;
    pdf.text(`Color Depth: ${screen.colorDepth} bits`, 25, yPos);

    // Footer
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'italic');
    pdf.text('This test page validates printer functionality including fonts, alignment, and graphics.', 105, 260, { align: 'center' });
    pdf.text(`Generated: ${currentTime.toISOString()}`, 105, 267, { align: 'center' });

    // Page border
    if (options.includeGraphics) {
      pdf.setLineWidth(0.5);
      pdf.rect(10, 10, 195, 267);
    }

    // Return as data URL (cleaned for UHB compatibility)
    return this.cleanDataUrl(pdf.output('dataurlstring'));
  }

  /**
   * Detect printer type based on printer name
   */
  static detectPrinterType(printerName: string): 'label' | 'letter' | 'unknown' {
    const name = printerName.toLowerCase();

    // Label printer keywords
    if (name.includes('munbyn') ||
        name.includes('zebra') ||
        name.includes('dymo') ||
        name.includes('label') ||
        name.includes('thermal') ||
        name.includes('itpp')) {
      return 'label';
    }

    // Letter printer keywords
    if (name.includes('hp') ||
        name.includes('canon') ||
        name.includes('epson') ||
        name.includes('brother') ||
        name.includes('laser') ||
        name.includes('inkjet') ||
        name.includes('officejet')) {
      return 'letter';
    }

    return 'unknown';
  }

  /**
   * Create a simple test with minimal content for quick testing
   */
  static generateSimpleTest(printerName: string, labelSize: LabelLayout = '4x6'): string {
    const dimensions = LABEL_DIMENSIONS[labelSize];

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [dimensions.widthMm, dimensions.heightMm]
    });

    const centerX = dimensions.widthMm / 2;

    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('TEST PRINT', centerX, 25, { align: 'center' });

    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(printerName, centerX, 40, { align: 'center' });

    const now = new Date();
    pdf.text(now.toLocaleString(), centerX, 55, { align: 'center' });

    pdf.setFontSize(10);
    pdf.text(`Label Size: ${labelSize}`, centerX, 70, { align: 'center' });
    pdf.text('If you can read this,', centerX, 85, { align: 'center' });
    pdf.text('your printer is working!', centerX, 95, { align: 'center' });

    return this.cleanDataUrl(pdf.output('dataurlstring'));
  }
}